'use client';

import { useState } from 'react';
import type { DefaultOptions, QueryClient } from '@tanstack/react-query';
import { isServer, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import type { CreateQueryClientOptions, MutationErrorContext, QueryErrorContext } from './createQueryClient';
import { createQueryClient } from './createQueryClient';

export { HydrationBoundary } from '@tanstack/react-query';
export * from '@tanstack/react-query-devtools';

let browserQueryClient: QueryClient | undefined;

/**
 * Returns a `QueryClient` suitable for client-side use.
 *
 * - On the server, returns a fresh client per call so RSC renders never share cache across requests.
 * - In the browser, returns a memoized singleton so React Fast Refresh / Suspense retries reuse the same cache.
 *
 * @see https://tanstack.com/query/latest/docs/framework/react/guides/advanced-ssr#nextjs-app-router
 */
export function getQueryClient(options?: CreateQueryClientOptions) {
  if (isServer) {
    return createQueryClient(options);
  }

  browserQueryClient ??= createQueryClient(options);
  return browserQueryClient;
}

export function QueryProvider({
  children,
  client,
  disableDevtools = false,
}: React.PropsWithChildren<{
  client: QueryClient;
  disableDevtools?: boolean;
}>) {
  return (
    <QueryClientProvider client={client}>
      {children}
      {disableDevtools ? null : <ReactQueryDevtools />}
    </QueryClientProvider>
  );
}

/**
 * @deprecated Use `QueryProvider` instead. This export is preserved so existing
 * Pages Router apps continue to compile without changes.
 */
export function DefaultQueryProvider({
  children,
  defaultOptions,
  queryErrorContext,
  mutationErrorContext,
  showDevtools = true,
}: React.PropsWithChildren<{
  defaultOptions?: DefaultOptions;
  queryErrorContext?: QueryErrorContext;
  mutationErrorContext?: MutationErrorContext;
  showDevtools?: boolean;
}>) {
  const [client] = useState(() => createQueryClient({ defaultOptions, queryErrorContext, mutationErrorContext }));

  return (
    <QueryProvider client={client} disableDevtools={!showDevtools}>
      {children}
    </QueryProvider>
  );
}
